import {
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  ClipboardText,
  Motorcycle,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { PageInfo } from '@/src/@types/models'
import { OfferTilesBlock } from '@/src/@types/statamic'
import Button from '@/src/components/core/Button'
import Content from '@/src/components/elements/Content'
import Picture from '@/src/components/elements/Picture'
import { Block } from '@/src/lib/PageBuilder'

const TileIconMap: { [key: string]: React.ReactNode } = {
  motorcycle: (
    <Motorcycle size={32} weight="fill" className="text-purple-900" />
  ),
  'clipboard-text': (
    <ClipboardText size={32} weight="fill" className="text-purple-900" />
  ),
  'arrows-clockwise': (
    <ArrowsClockwise size={32} weight="fill" className="text-purple-900" />
  ),
}

export default function Component({
  optional_title: title,
  text,
  offer_tiles,
  numbered,
  pageInfo,
  blocks,
  index,
}: OfferTilesBlock & {
  pageInfo: PageInfo
  blocks: Block[]
  index: number
}) {
  return (
    <div
      className={clsx({
        'md:pb-12 lg:pb-16': !pageInfo.newSpacing,
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <div className="mx-auto max-w-container px-5 md:px-6">
        {title && (
          <h2
            className={clsx('text-4xl font-black text-balance', {
              'md:text-20xl': blocks[index - 1]?.block !== 'offers',
              'md:text-10xl': blocks[index - 1]?.block === 'offers',
            })}
          >
            {title}
          </h2>
        )}
        {text && <Content content={text} className="mt-4 lg:max-w-[66%]" />}
      </div>
      <div className="mt-6 lg:hidden">
        <Mobile offer_tiles={offer_tiles} numbered={numbered} />
      </div>
      <div className="mx-auto mt-10 hidden max-w-container px-6 lg:block">
        <Desktop offer_tiles={offer_tiles} numbered={numbered} />
      </div>
    </div>
  )
}

function Mobile({
  offer_tiles,
  numbered,
}: {
  offer_tiles: OfferTilesBlock['offer_tiles']
  numbered: OfferTilesBlock['numbered']
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentIndex(emblaApi.selectedScrollSnap())
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <>
      <div className="mt-6 overflow-hidden" ref={emblaRef}>
        <div className="flex select-none">
          {offer_tiles.map((offer, index) => (
            <Tile
              number={numbered ? index + 1 : undefined}
              className="ml-5 min-w-0 flex-[0_0_80%] last:mr-5"
              {...offer}
            />
          ))}
        </div>
      </div>
      <div className="mx-5 mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {currentIndex + 1} von {offer_tiles.length}
        </p>
        {!(prevDisabled && nextDisabled) && (
          <div className="space-x-3">
            <Button
              size="icon-small"
              icon={<CaretLeft weight="bold" />}
              onClick={prev}
              version="gray-outlined"
              disabled={prevDisabled}
            />
            <Button
              size="icon-small"
              icon={<CaretRight weight="bold" />}
              onClick={next}
              version="gray-outlined"
              disabled={nextDisabled}
            />
          </div>
        )}
      </div>
    </>
  )
}

function Desktop({
  offer_tiles,
  numbered,
}: {
  offer_tiles: OfferTilesBlock['offer_tiles']
  numbered: OfferTilesBlock['numbered']
}) {
  return (
    <div className="grid grid-cols-3 gap-6">
      {offer_tiles.map((offer, index) => (
        <Tile
          number={numbered ? index + 1 : undefined}
          key={offer.title}
          {...offer}
        />
      ))}
    </div>
  )
}

function Tile({
  number,
  image,
  icon,
  title,
  subtitle,
  text,
  link_link,
  link_text,
  className,
}: {
  number?: number
  className?: string
} & OfferTilesBlock['offer_tiles'][0]) {
  return (
    <div
      className={clsx(
        'flex min-h-70 flex-col justify-between rounded-xl',
        {
          'bg-gray-100 p-6': !image,
        },
        className
      )}
    >
      <div>
        {image && (
          <div className="relative mb-6">
            <div className="aspect-w-4 aspect-h-3">
              <Picture
                priority
                {...image}
                fill
                className="rounded-xl object-cover"
              />
            </div>
            {number !== undefined && (
              <div className="absolute top-3 left-3 flex size-12 items-center justify-center rounded-full bg-white shadow-xs">
                <p className="text-4xl leading-[26px] font-black text-gray-800">
                  {number}
                </p>
              </div>
            )}
          </div>
        )}
        {!image && icon && (
          <div className="mb-12 flex size-16 items-center justify-center justify-self-start rounded-lg border border-gray-200 bg-white">
            {TileIconMap[icon.value]}
          </div>
        )}
        <h3 className="text-xl font-black md:text-2xl">{title}</h3>
        {subtitle && <p className="mt-2 font-bold">{subtitle}</p>}
        <Content
          content={text}
          textSize="allBase"
          className="mt-3 text-gray-800"
        />
      </div>
      {link_link?.url && (
        <Link
          href={link_link.url}
          className="mt-4 inline-block font-bold text-purple-600"
        >
          {link_text || 'Mehr erfahren'}
        </Link>
      )}
    </div>
  )
}
